// src/components/app/AppErrorBoundary.jsx
// Global error boundary with diagnostics and auto-recovery
import React from 'react';
import { Helmet } from 'react-helmet-async';

export default class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Check for chunk load failure (deployment update)
    if (error?.message?.includes('Failed to fetch dynamically imported module') || 
        error?.message?.includes('Importing a module script failed')) {
      const storageKey = 'app_reload_chunk_error';
      const lastReload = sessionStorage.getItem(storageKey);
      
      // If we haven't reloaded for this recently (within 10 seconds), try a reload
      if (!lastReload || Date.now() - parseInt(lastReload) > 10000) {
        console.log('[AppErrorBoundary] Detected chunk load error, forcing reload...');
        sessionStorage.setItem(storageKey, Date.now().toString());
        window.location.reload();
        return;
      }
    }

    this.setState({ errorInfo });
    
    // Log to console with context
    console.error('[AppErrorBoundary] React error caught:', {
      error: error?.message,
      stack: error?.stack,
      componentStack: errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      buildVersion: import.meta.env.VITE_BUILD_VERSION || 'dev',
    });

    // Store in localStorage for diagnostics (ring buffer)
    try {
      const errors = JSON.parse(localStorage.getItem('app_errors') || '[]');
      errors.push({
        message: error?.message,
        stack: error?.stack?.substring(0, 500), // Truncate
        url: window.location.href,
        timestamp: new Date().toISOString(),
      });
      // Keep last 20 errors
      if (errors.length > 20) errors.shift();
      localStorage.setItem('app_errors', JSON.stringify(errors));
    } catch {}
  }

  copyDiagnostics = () => {
    const diagnostics = {
      error: this.state.error?.message,
      stack: this.state.error?.stack,
      componentStack: this.state.errorInfo?.componentStack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      buildVersion: import.meta.env.VITE_BUILD_VERSION || 'dev',
      recentErrors: JSON.parse(localStorage.getItem('app_errors') || '[]'),
    };

    navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2))
      .then(() => alert('Diagnostics copied to clipboard!'))
      .catch(() => {
        // Fallback: show in alert
        prompt('Copy diagnostics:', JSON.stringify(diagnostics, null, 2));
      });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-[#0b0b12] text-white p-6">
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
            <meta name="googlebot" content="noindex, nofollow" />
            <title>Error - AI Hairstyle Studio</title>
          </Helmet>
          <div className="max-w-2xl w-full bg-[#14141c] border border-white/10 rounded-2xl p-8">
            <div className="text-center mb-6">
              <h1 className="text-3xl font-bold mb-3">Something went wrong</h1>
              <p className="text-white/70 text-lg mb-2">
                We encountered an unexpected error. This has been logged.
              </p>
              <p className="text-white/50 text-sm">
                {this.state.error?.message || 'An unexpected error occurred'}
              </p>
            </div>

            <div className="flex gap-3 justify-center mb-6">
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 font-semibold transition-colors"
              >
                🔄 Reload Page
              </button>
              <button
                onClick={this.copyDiagnostics}
                className="px-6 py-3 rounded-lg bg-white/10 hover:bg-white/20 font-semibold transition-colors"
              >
                📋 Copy Diagnostics
              </button>
            </div>

            <details className="mt-6 text-xs">
              <summary className="cursor-pointer text-white/50 hover:text-white/70 mb-2">
                🔍 Technical Details
              </summary>
              <div className="bg-black/30 p-4 rounded-lg overflow-auto max-h-64 text-white/70 font-mono">
                <div className="mb-2">
                  <strong>Error:</strong> {this.state.error?.message}
                </div>
                {this.state.error?.stack && (
                  <div className="mb-2">
                    <strong>Stack:</strong>
                    <pre className="mt-1 text-[10px] whitespace-pre-wrap">
                      {this.state.error.stack}
                    </pre>
                  </div>
                )}
                {this.state.errorInfo?.componentStack && (
                  <div>
                    <strong>Component Stack:</strong>
                    <pre className="mt-1 text-[10px] whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </div>
                )}
                <div className="mt-2 pt-2 border-t border-white/10">
                  <div><strong>URL:</strong> {window.location.href}</div>
                  <div><strong>Time:</strong> {new Date().toISOString()}</div>
                  <div><strong>Build:</strong> {import.meta.env.VITE_BUILD_VERSION || 'dev'}</div>
                </div>
              </div>
            </details>

            <div className="mt-6 text-center text-xs text-white/40">
              If this persists, try clearing your browser cache or contact support.
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
